import * as React from 'react';
import * as api from '../services/apiService';
import type { Notification } from '../types';
import { useAuth } from './AuthProvider';
import { useUI } from './UIProvider';
import { useNavigation } from './NavigationProvider';

const { createContext, useState, useCallback, useMemo, useContext, useEffect, useRef } = React;

interface NotificationsContextState {
  notifications: Notification[];
  unreadNotificationsCount: number;
  handleMarkNotificationsRead: () => Promise<void>;
}
const NotificationsContext = createContext<NotificationsContextState | null>(null);

export const useNotifications = () => {
    const context = useContext(NotificationsContext);
    if (!context) throw new Error('useNotifications must be used within a NotificationsProvider');
    return context;
};

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { currentUser, authStatus } = useAuth();
    const { showToast } = useUI();
    const { refreshBands } = useNavigation();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    
    // We use a ref to track the last notification timestamp we successfully processed
    const lastProcessedIdRef = useRef<string | null>(null);

    const refreshNotifications = useCallback(async (fullSync = false) => {
        if (authStatus !== 'authenticated') return [];
        try {
            const since = undefined; // Fetch all relevant for context merging
            const newNotifs = await api.getNotifications(since);
            
            if (newNotifs.length > 0) {
                 setNotifications(prev => {
                    const existingIds = new Set(prev.map(n => n.id));
                    const uniqueNew = newNotifs.filter(n => !existingIds.has(n.id));
                    const combined = [...uniqueNew, ...prev].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
                    return combined;
                 });
                 return newNotifs;
            } else if (fullSync) {
                setNotifications([]);
            }
            return [];
        } catch(e) {
            console.error("Failed to refresh notifications", e);
            return [];
        }
    }, [authStatus]);

    // Initial fetch
    useEffect(() => {
        if (authStatus === 'authenticated' && currentUser && !currentUser.needsOnboarding) {
            refreshNotifications(true).then(list => {
              if (list.length > 0) lastProcessedIdRef.current = list[0].id;
            });
        }
    }, [authStatus, currentUser, refreshNotifications]);

    // Polling fetch for real-time alerts
    useEffect(() => {
        if (authStatus !== 'authenticated' || !currentUser || currentUser.needsOnboarding) return;

        const pollId = setInterval(async () => {
            const allNotifications = await refreshNotifications(false);
            
            // Detect actually "new" notifications based on the top ID
            if (allNotifications.length > 0) {
                const latest = allNotifications[0];
                
                if (latest.id !== lastProcessedIdRef.current) {
                    lastProcessedIdRef.current = latest.id;
                    
                    // Only show toast if it's an unread notification from another user
                    if (!latest.isRead && latest.user.handle !== currentUser.handle) {
                        let message = 'You have a new notification!';
                        switch(latest.type) {
                            case 'endorse': message = `${latest.user.name} endorsed you!`; break;
                            case 'comment': message = `${latest.user.name} commented on your post.`; break;
                            case 'application': message = `${latest.user.name} applied to your opportunity.`; break;
                            case 'match': message = `New opportunity recommended for you!`; break;
                            case 'accepted': message = `Your application for "${latest.opportunity?.title}" was accepted!`; break;
                            case 'message': message = `New message from ${latest.user.name}.`; break;
                            case 'create_band_prompt':
                                message = `A new band page for "${latest.band?.name}" has been created!`;
                                refreshBands();
                                break;
                            case 'attendance': message = `${latest.user.name} is attending your event "${latest.opportunity?.title}".`; break;
                        }
                        showToast(message);
                    }
                }
            }
        }, 15000); // Polling every 15 seconds for snappier feedback

        return () => clearInterval(pollId);
    }, [authStatus, currentUser, refreshNotifications, showToast, refreshBands]);

    const unreadNotificationsCount = useMemo(() => notifications.filter(n => !n.isRead).length, [notifications]);

    const handleMarkNotificationsRead = useCallback(async () => {
        if (authStatus !== 'authenticated') return;
        const hasUnread = notifications.some(n => !n.isRead);
        if (!hasUnread) return;

        setNotifications(prev => prev.map(n => ({...n, isRead: true})));
        try {
            await api.markNotificationsRead();
        } catch (error) {
            console.error("Failed to mark notifications as read on server:", error);
        }
    }, [notifications, authStatus]);

    const value = { notifications, unreadNotificationsCount, handleMarkNotificationsRead };

    return (
        <NotificationsContext.Provider value={value}>
            {children}
        </NotificationsContext.Provider>
    );
};